import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Plus, Search, Trash2, X } from "lucide-react";
import { createUser, deleteUsers, getUsers } from "../../../services/admin/users";
import toast from "react-hot-toast"; // optional: for feedback

interface User {
    id: number;
    username: string;
    email: string;
}

function AdminUsers() {
    const [users, setUsers] = useState<User[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newUser, setNewUser] = useState({
        username: "",
        email: "",
        password: "",
    });
    const [creating, setCreating] = useState(false);

    const loadUsers = async () => {
        setIsLoading(true);
        const res = await getUsers();
        if (res.success && Array.isArray(res.data)) {
            setUsers(res.data);
        } else {
            console.error("Failed to load users", res.error || res.data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const filteredUsers = users.filter((user) =>
        (user.username?.toLowerCase() || "").includes(search.toLowerCase()) ||
        (user.email?.toLowerCase() || "").includes(search.toLowerCase())
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewUser({ ...newUser, [e.target.name]: e.target.value });
    };

    const handleAddUser = async () => {
        const { username, email, password } = newUser;

        if (!username || !email || !password) {
            toast.error("All fields are required");
            return;
        }

        setCreating(true);

        const formData = new FormData();
        formData.append("username", username);
        formData.append("email", email);
        formData.append("password", password);

        const res = await createUser(formData);

        setCreating(false);

        if (res.success) {
            toast.success("User created successfully");
            setShowModal(false);
            setNewUser({ username: "", email: "", password: "" });

            // Delay & reload
            await new Promise((r) => setTimeout(r, 500));
            loadUsers();
        } else {
            toast.error(res.error || "Failed to create user");
        }
    };

    const handleDeleteUser = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        const res = await deleteUsers(id);
        if (res.success) {
            toast.success("User deleted successfully");
            loadUsers();
        } else {
            toast.error(res.error || "Failed to delete user");
        }
    };

    const columns: TableColumn<User>[] = [
        {
            name: "#",
            cell: (_row, index) => (index + 1).toString(),
        },
        {
            name: "Name",
            selector: (row) => row.username,
            sortable: true,
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row, rowIndex) => (
                <>
                    <button
                        onClick={() => handleDeleteUser(row.id)}
                        className={`text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </>
            ),
            ignoreRowClick: true,
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">Users</h1>

            {/* Search & Add */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>

                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#009FE3] text-white rounded-lg hover:opacity-90 transition whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add User
                </button>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredUsers}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                    noDataComponent={<div className="p-4">No users found.</div>}
                />
            </div>

            {/* Add User Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h2 className="text-lg font-semibold mb-4">Add New User</h2>
                        <div className="space-y-4">
                            <input
                                type="text"
                                name="username"
                                placeholder="Username"
                                value={newUser.username}
                                onChange={handleInputChange}
                                className="w-full border rounded px-4 py-2 text-sm"
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={newUser.email}
                                onChange={handleInputChange}
                                className="w-full border rounded px-4 py-2 text-sm"
                            />
                            <input
                                type="text"
                                name="password"
                                placeholder="Password"
                                value={newUser.password}
                                onChange={handleInputChange}
                                className="w-full border rounded px-4 py-2 text-sm"
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-gray-600 hover:text-black"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddUser}
                                disabled={creating}
                                className="px-4 py-2 bg-[#009FE3] text-white rounded hover:opacity-90"
                            >
                                {creating ? "Adding..." : "Add User"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminUsers;
